/* Built by Alpas Made. */
/*
 * The download popup. ONE implementation, shared by both surfaces that hand out
 * a file: the button on a product page (credit-download.js) and My Library on
 * the membership dashboard (credit-dashboard.js). Two copies would drift, and
 * the dashboard drifting is exactly how a member would end up back on the old
 * behaviour without anyone noticing.
 *
 * Why a popup at all. Firing two automatic downloads in a row (the design, then
 * the guide) does not work: Chromium gates the SECOND one behind a permission
 * prompt, and which of the two survives varies, so a member can pay credits
 * and receive the instructions PDF with no design file. Here each file is one
 * click by the person, and a browser never blocks those.
 *
 * It also delivers what the listing promises. 354 live products keep several
 * files in their folder and advertise all of them; the old code signed one key
 * from a metafield and handed over one of three.
 *
 * window.KCDownloadPopup.open({ files, charged, alreadyOwned, balance, title, returnFocus })
 *
 * returnFocus is optional: the element that gets focus back when the list
 * closes, usually the button that opened it.
 */
(function () {
  'use strict';

  var ID = 'KCDownloadPopup';
  if (window[ID]) return;

  var GUIDE_COPY = 'The KC file guide explains which file suits which machine.';

  function el(html) {
    var d = document.createElement('div');
    d.innerHTML = html.trim();
    return d.firstChild;
  }

  function esc(s) {
    var d = document.createElement('span');
    d.textContent = s == null ? '' : s;
    return d.innerHTML;
  }

  function size(bytes) {
    if (!bytes && bytes !== 0) return '';
    return bytes >= 1048576
      ? (bytes / 1048576).toFixed(1) + ' MB'
      : Math.max(1, Math.round(bytes / 1024)) + ' KB';
  }

  /* An anchor click, not location.assign: assign cancels any request already in
     flight, so a person clicking two files quickly would lose the first. */
  function grab(url, name) {
    var a = document.createElement('a');
    a.href = url;
    if (name) a.download = name;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    window.setTimeout(function () { document.body.removeChild(a); }, 1000);
  }

  var root = null;
  var expiryTimer = null;
  var returnFocus = null;

  /*
   * One Escape closes one thing. The dialog closes on the Escape keydown (its
   * cancel event), but the theme closes every open modal on the Escape KEYUP
   * that follows (a keyup listener on document in theme.js). Over a quick
   * popup that one press would close the file list AND the popup behind it.
   * So when the dialog is cancelled, the next Escape keyup is stopped on
   * window, in the capture phase, before it reaches document. Other keys pass
   * untouched, and leaving the window drops the guard, so it can never eat a
   * later, separate Escape.
   */
  function swallowNextEscapeKeyup() {
    function done() {
      window.removeEventListener('keyup', onKeyup, true);
      window.removeEventListener('blur', done);
    }
    function onKeyup(e) {
      if (e.key !== 'Escape' && e.key !== 'Esc' && e.code !== 'Escape') return;
      e.stopPropagation();
      done();
    }
    window.addEventListener('keyup', onKeyup, true);
    window.addEventListener('blur', done);
  }

  /*
   * The links die after their TTL. Left open past that, every button would fail
   * silently with an R2 error page, which is the kind of thing that becomes a
   * support message rather than a bug report. So the popup says so itself, and
   * says the thing people need to hear: pressing download again is free.
   */
  function expire() {
    if (!root) return;
    root.querySelectorAll('.kcdl__get, .kcdl__guide-btn').forEach(function (b) {
      b.disabled = true;
    });
    var body = root.querySelector('.kcdl__body');
    if (body && !body.querySelector('.kcdl__expired')) {
      var p = document.createElement('p');
      p.className = 'kcdl__expired';
      p.setAttribute('role', 'status');
      p.textContent = 'These links have expired. Close this and press download again, ' +
        'it will not cost another credit.';
      body.insertBefore(p, body.firstChild);
      body.scrollTop = 0;
    }
  }

  /*
   * A real <dialog> opened with showModal(), not a fixed-position div.
   *
   * The div version measured 1073px wide on a 375px phone: a fixed element is
   * sized by its containing block, and on this storefront that resolved to
   * something far wider than the viewport, so the popup rendered off-screen and
   * wider than the device. A dialog in the top layer is sized against the
   * viewport by definition, and no ancestor can move it.
   *
   * It also brings Escape, the focus trap, focus restore and the backdrop for
   * free, which is four things that were hand-written and could rot.
   */
  function close() {
    if (expiryTimer) { window.clearTimeout(expiryTimer); expiryTimer = null; }
    if (!root) return;
    if (root.open) root.close();
    root.remove();
    root = null;
    document.documentElement.style.overflow = '';

    // Back to where the person was, if that element is still on the page.
    // Without this, focus fell to <body>, outside any popup behind the list.
    var target = returnFocus;
    returnFocus = null;
    if (target && target.isConnected && typeof target.focus === 'function') {
      try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
    }
  }

  function open(data) {
    returnFocus = null;
    close();
    returnFocus = data.returnFocus || null;

    var files = (data.files || []).filter(function (f) { return f && f.url && !f.isGuide; });
    var guide = (data.files || []).filter(function (f) { return f && f.url && f.isGuide; })[0];
    var n = files.length;

    var charge = data.alreadyOwned
      ? 'No credits used, you already own this.'
      : (data.charged || 0) + (data.charged === 1 ? ' credit used' : ' credits used');

    var rows = files.map(function (f) {
      return '<div class="kcdl__row">' +
        '<span class="kcdl__meta">' +
          '<span class="kcdl__name">' + esc(f.name) + '</span>' +
          (f.bytes ? '<span class="kcdl__size">' + esc(size(f.bytes)) + '</span>' : '') +
        '</span>' +
        '<button class="kcdl__get" type="button" data-url="' + esc(f.url) + '" ' +
          'data-name="' + esc(f.name) + '">Download</button>' +
      '</div>';
    }).join('');

    root = el(
      '<dialog class="kcdl" aria-labelledby="kcdlTitle">' +
        '<div class="kcdl__panel">' +
          '<button class="kcdl__x" type="button" data-close aria-label="Close">&#10005;</button>' +
          '<div class="kcdl__head">' +
            '<span class="kcdl__eyebrow">Your download</span>' +
            '<h2 class="kcdl__title" id="kcdlTitle">' + n +
              (n === 1 ? ' file is ready' : ' files are ready') + '</h2>' +
            (data.title ? '<p class="kcdl__for">' + esc(data.title) + '</p>' : '') +
          '</div>' +
          '<div class="kcdl__charge">' +
            '<b>' + esc(charge) + '</b>' +
            '<span>Taking every file costs nothing more.' +
              (data.expiresIn ? ' Links stay live for ' + Math.round(data.expiresIn / 60) +
                ' minutes.' : '') + '</span>' +
          '</div>' +
          '<div class="kcdl__body">' +
            '<div class="kcdl__list">' + rows + '</div>' +
            (guide
              ? '<div class="kcdl__guide">' +
                  '<span class="kcdl__guide-tx"><b>Not sure which file to use?</b>' +
                    GUIDE_COPY + '</span>' +
                  '<button class="kcdl__guide-btn" type="button" data-url="' + esc(guide.url) + '" ' +
                    'data-name="' + esc(guide.name) + '">Get the guide (PDF)</button>' +
                '</div>'
              : '') +
          '</div>' +
          '<div class="kcdl__foot">These files are yours for good. You can open this list again ' +
            'any time from <b>My Membership</b>, and it will never charge you twice.</div>' +
        '</div>' +
      '</dialog>'
    );

    // Clicking the backdrop lands on the dialog itself, never on the panel.
    root.addEventListener('click', function (e) {
      if (e.target === root || e.target.closest('[data-close]')) { close(); return; }
      var btn = e.target.closest('.kcdl__get, .kcdl__guide-btn');
      if (!btn) return;
      grab(btn.dataset.url, btn.dataset.name);
      if (btn.classList.contains('kcdl__get')) {
        btn.closest('.kcdl__row').classList.add('is-done');
        btn.textContent = 'Download again';
      }
    });

    document.body.appendChild(root);
    root.addEventListener('cancel', swallowNextEscapeKeyup);
    root.addEventListener('close', close);
    if (root.showModal) {
      root.showModal();
    } else {
      root.setAttribute('open', '');
      root.setAttribute('role', 'dialog');
      root.setAttribute('aria-modal', 'true');
    }
    document.documentElement.style.overflow = 'hidden';
    var firstBtn = root.querySelector('.kcdl__get');
    if (firstBtn) firstBtn.focus();

    // A little early, so nobody presses a link in the second it dies.
    if (data.expiresIn) {
      expiryTimer = window.setTimeout(expire, Math.max(5, data.expiresIn - 10) * 1000);
    }
  }

  window[ID] = { open: open, close: close };
})();
